/**
 * useActionItemSignalR.js
 * 
 * Custom React hook for real-time Action Item updates via SignalR.
 * 
 * This hook establishes a persistent WebSocket connection to the server's SignalR hub
 * and provides callbacks for when action items are updated. It automatically:
 * - Manages connection lifecycle (connect on mount, disconnect on unmount)
 * - Handles reconnection with exponential backoff
 * - Joins the user's personal group for targeted notifications
 * - Optionally joins project groups for project-specific updates
 * 
 * Usage:
 * ```jsx
 * const { isConnected, joinProjectGroup, leaveProjectGroup } = useActionItemSignalR({
 *   onActionItemUpdated: (update) => { console.log('Item updated:', update); },
 *   onStatusChange: (update) => { console.log('Status changed:', update); },
 *   enabled: true, // Set to false to disable the connection
 * });
 * ```
 */

import { useState, useEffect, useCallback, useRef, startTransition } from 'react';
import { HubConnectionBuilder, LogLevel, HubConnectionState } from '@microsoft/signalr';
import { useAuth } from '@/context/AuthContext';
import * as Sentry from '@sentry/react';

/**
 * Configuration options for the SignalR hook
 */
const DEFAULT_OPTIONS = {
    /** Enable or disable the SignalR connection */
    enabled: true,
    /** Log level for SignalR (None, Error, Warning, Information, Debug, Trace) */
    logLevel: LogLevel.Warning,
    /** Automatic reconnect delays in milliseconds */
    reconnectDelays: [0, 2000, 5000, 10000, 30000],
};

/**
 * Custom hook for real-time Action Item updates via SignalR
 * 
 * @param {Object} options - Configuration options
 * @param {Function} options.onActionItemUpdated - Callback for any action item update
 * @param {Function} options.onStatusChange - Callback specifically for status changes
 * @param {Function} options.onAssignmentChange - Callback for assignment changes
 * @param {Function} options.onProjectTaskUpdated - Callback for project task updates
 * @param {Function} options.onNeedsAttention - Callback when an item needs the user's attention
 * @param {Function} options.onAssignedToYou - Callback when an item is assigned to the user
 * @param {boolean} options.enabled - Whether the connection should be active
 * @returns {Object} Hook state and methods
 */
const useActionItemSignalR = (options = {}) => {
    const { user } = useAuth();
    const connectionRef = useRef(null);
    const callbacksRef = useRef({});
    const [isConnected, setIsConnected] = useState(false);
    const [connectionError, setConnectionError] = useState(null);
    const [lastUpdate, setLastUpdate] = useState(null);

    const {
        onActionItemUpdated,
        onStatusChange,
        onAssignmentChange,
        onProjectTaskUpdated,
        onNeedsAttention,
        onAssignedToYou,
        onUnassignedFromYou,
        enabled = DEFAULT_OPTIONS.enabled,
    } = options;
    
    // Update callbacks ref whenever they change (without recreating connection)
    useEffect(() => {
        callbacksRef.current = {
            onActionItemUpdated,
            onStatusChange,
            onAssignmentChange,
            onProjectTaskUpdated,
            onNeedsAttention,
            onAssignedToYou,
            onUnassignedFromYou,
        };
    }, [onActionItemUpdated, onStatusChange, onAssignmentChange, onProjectTaskUpdated, onNeedsAttention, onAssignedToYou, onUnassignedFromYou]);

    /**
     * Checks whether the stored JWT token is expired
     * @returns {boolean} true if the token is missing or expired
     */
    const isTokenExpired = useCallback(() => {
        try {
            const storedUser = JSON.parse(localStorage.getItem('user'));
            if (!storedUser?.token) return true;
            const payload = JSON.parse(atob(storedUser.token.split('.')[1]));
            return Date.now() >= payload.exp * 1000;
        } catch (catchError) {
            Sentry.captureException(catchError);
            return true;
        }
    }, []);

    /**
     * Gets the auth token for SignalR connection
     */
    const getAccessToken = useCallback(() => {
        try {
            const storedUser = JSON.parse(localStorage.getItem('user'));
            return storedUser?.token || '';
        } catch (catchError) {
            Sentry.captureException(catchError);
            return '';
        }
    }, []);

    /**
     * Joins a project group to receive project-specific updates
     * @param {number} projectId - The project ID to join
     */
    const joinProjectGroup = useCallback(async (projectId) => {
        if (connectionRef.current?.state === HubConnectionState.Connected) {
            try {
                await connectionRef.current.invoke('JoinProjectGroup', projectId);
                console.debug(`Joined project group: ${projectId}`);
            } catch (err) {
                Sentry.captureException(err);
                console.error('Failed to join project group:', err);
            }
        }
    }, []);

    /**
     * Leaves a project group
     * @param {number} projectId - The project ID to leave
     */
    const leaveProjectGroup = useCallback(async (projectId) => {
        if (connectionRef.current?.state === HubConnectionState.Connected) {
            try {
                await connectionRef.current.invoke('LeaveProjectGroup', projectId);
                console.debug(`Left project group: ${projectId}`);
            } catch (err) {
                Sentry.captureException(err);
                console.error('Failed to leave project group:', err);
            }
        }
    }, []);

    /**
     * Sends a ping to verify connection is alive
     * @returns {Promise<Date>} Server timestamp
     */
    const ping = useCallback(async () => {
        if (connectionRef.current?.state === HubConnectionState.Connected) {
            try {
                return await connectionRef.current.invoke('Ping');
            } catch (err) {
                Sentry.captureException(err);
                console.error('Ping failed:', err);
                return null;
            }
        }
        return null;
    }, []);

    // Initialize and manage SignalR connection
    useEffect(() => {
        // Don't connect if disabled, no user, or token is already expired
        if (!enabled || !user?.userID || isTokenExpired()) {
            return;
        }

        // Build the connection.
        // Hub path is /api/hubs/actionitems so that production Nginx (which already has
        // WebSocket upgrade headers on the /api/ location block) forwards the request
        // correctly.  Using /hubs/ at the root bypasses that block and produces a 404.
        const connection = new HubConnectionBuilder()
            .withUrl('/api/hubs/actionitems', {
                accessTokenFactory: getAccessToken,
            })
            .withAutomaticReconnect(DEFAULT_OPTIONS.reconnectDelays)
            .configureLogging(DEFAULT_OPTIONS.logLevel)
            .build();

        connectionRef.current = connection;

        // Set up event handlers
        connection.on('ActionItemUpdated', (update) => {
            console.log('[SignalR Hub] ActionItemUpdated event received:', update);
            startTransition(() =>
                setLastUpdate({ type: 'ActionItemUpdated', data: update, timestamp: new Date() })
            );
            callbacksRef.current.onActionItemUpdated?.(update);
            
            // Route to specific handlers based on update type
            if (update.Type === 'StatusChange') {
                callbacksRef.current.onStatusChange?.(update);
            } else if (update.Type === 'AssignmentChange') {
                callbacksRef.current.onAssignmentChange?.(update);
            }
        });

        connection.on('ProjectTaskUpdated', (update) => {
            startTransition(() =>
                setLastUpdate({ type: 'ProjectTaskUpdated', data: update, timestamp: new Date() })
            );
            callbacksRef.current.onProjectTaskUpdated?.(update);
        });

        connection.on('ActionItemNeedsAttention', (update) => {
            startTransition(() =>
                setLastUpdate({ type: 'NeedsAttention', data: update, timestamp: new Date() })
            );
            callbacksRef.current.onNeedsAttention?.(update);
        });

        connection.on('ActionItemAssignedToYou', (update) => {
            startTransition(() =>
                setLastUpdate({ type: 'AssignedToYou', data: update, timestamp: new Date() })
            );
            callbacksRef.current.onAssignedToYou?.(update);
        });

        connection.on('ActionItemUnassignedFromYou', (update) => {
            startTransition(() =>
                setLastUpdate({ type: 'UnassignedFromYou', data: update, timestamp: new Date() })
            );
            callbacksRef.current.onUnassignedFromYou?.(update);
        });

        connection.on('Pong', (serverTime) => {
            console.debug('Pong received:', serverTime);
        });

        // Connection state handlers
        connection.onclose((error) => {
            startTransition(() => setIsConnected(false));
            if (error) {
                const errMsg = error?.message || error?.toString() || '';
                const isAuthError = errMsg.includes('401') || errMsg.includes('Token expired') || errMsg.includes('Unauthorized');
                if (isAuthError) {
                    console.debug('SignalR connection closed — session expired or unauthorized');
                } else {
                    console.error('SignalR connection closed with error:', error);
                }
                startTransition(() => setConnectionError(error.message));
            }
        });

        connection.onreconnecting((error) => {
            startTransition(() => setIsConnected(false));
            console.debug('SignalR reconnecting...', error);
        });

        connection.onreconnected((connectionId) => {
            startTransition(() => {
                setIsConnected(true);
                setConnectionError(null);
            });
            console.debug('SignalR reconnected with connectionId:', connectionId);
        });

        // Start the connection
        const startConnection = async () => {
            try {
                await connection.start();
                setIsConnected(true);
                setConnectionError(null);
                console.debug('SignalR connected successfully');
            } catch (err) {
                Sentry.captureException(err);
                // Detect 401 / token-expired errors from the SignalR negotiate request
                // and handle them silently — the auth layer will redirect to login
                const errMsg = err?.message || err?.toString() || '';
                if (errMsg.includes('Status code \'401\'') || errMsg.includes('Token expired') || errMsg.includes('Unauthorized')) {
                    console.debug('SignalR connection skipped — session expired or unauthorized');
                } else {
                    console.error('SignalR connection failed:', err);
                }
                setConnectionError(err.message);
                setIsConnected(false);
            }
        };

        startConnection();

        // Cleanup on unmount or when dependencies change
        return () => {
            if (connection) {
                connection.stop().catch(err => console.debug('Error stopping SignalR:', err));
            }
            connectionRef.current = null;
        };
    }, [
        enabled,
        user?.userID,
        getAccessToken,
        isTokenExpired,
        // Note: callbacks are NOT in dependencies - they're accessed via callbacksRef
    ]);

    return {
        /** Whether the SignalR connection is currently active */
        isConnected,
        /** Any connection error message */
        connectionError,
        /** The last update received from SignalR */
        lastUpdate,
        /** Join a project group for project-specific updates */
        joinProjectGroup,
        /** Leave a project group */
        leaveProjectGroup,
        /** Send a ping to verify connection */
        ping,
        // Note: connection ref not exposed to avoid render-time ref access issues
        // Use the methods provided (joinProjectGroup, leaveProjectGroup, ping) instead
    };
};

export default useActionItemSignalR;
